<template>
  <div class="px-4">
    <div v-if="!isShortInfo" class="border-b border-card-outline py-4 w-full flex items-center justify-between">
      <div>
        <p class="font-bold fs-16 flex">
          <StationImg :brand="event.information?.poi.brand" class="mr-2" aria-hidden="true" />{{ event.information?.poi.brand }}
        </p>
        <div class="fs-12">
          {{ getFullAddress }}
        </div>
      </div>
      <RPButton
        theme="custom"
        :name="$t('a11y.history.payment_details.drive_to_station')" 
        class="mr-0 ml-auto justify-end p-0 rounded-none w-fit overflow-visible"
        @click="goToNavigation"
      >
        <RPIcon type="navIcon" noFill />
      </RPButton>
    </div>
    <div class="border-b border-card-outline py-4 w-full space-y-2">
      <p class="flex justify-between w-full font-medium">
        <span>{{ t('history.details.order_id') }}</span>
        <span class="font-normal text-tertiary-text text-right">{{ event.information.orderId }}</span>
      </p>
      <p class="flex justify-between w-full font-medium">
        <span>{{ t('history.details.time_stamp') }}</span>
        <span class="font-normal text-right">{{ formatDate.weekday }} {{ formatDate.date }} - {{ formatDate.time }}</span>
      </p>

      <template v-if="!isShortInfo">
        <p class="flex justify-between w-full font-medium">
          <span>{{ isWash ? t('history.details.payment.wash.package') : t('history.details.fuel_type') }}</span>
          <span class="font-normal text-right">{{ event.information.items[0].details.name }}</span>
        </p>
        <p v-if="!isWash" class="flex justify-between w-full font-medium">
          <span>{{ t('history.details.pump') }}</span>
          <span class="font-normal">{{ event.information.items[0].details.pumpNumber }}</span>
        </p>
      </template>
    </div>

    <!-- Pump info -->
    <template v-if="!isShortInfo">
      <div v-if="authStore.preferredVehicle.package !== 'APP_ONLY'" class="border-b border-card-outline py-4 w-full">
        <p class="flex justify-between w-full font-medium">
          <span>{{ t('history.details.mileage') }}</span>
          <span class="font-normal">{{ formatMileage }}</span>
        </p>
      </div>
      <div v-if="vouchers.length && event.information.totalBeforeDiscounts" class="border-b border-card-outline py-4 w-full fs-18">
        <p class="flex justify-between w-full font-medium">
          <span>{{ t('history.details.amount') }}</span>
          <span v-if="event.information.price?.isPriceHidden" class="font-normal"> --.-</span>

          <span v-else class="font-normal">
            {{ currencyString(event.information.totalBeforeDiscounts.amount, event.information.totalBeforeDiscounts.currency) }}
          </span>
        </p>
      </div>
      <div class="border-b border-card-outline py-4 w-full space-y-2">
        <p class="flex justify-between w-full font-medium">
          <span>{{ t('history.details.tank') }}</span>
          <span class="font-normal">{{ event.information.items[0].details.quantity }} l</span>
        </p>
        <p class="flex justify-between w-full font-medium">
          <span>{{ t('history.details.unit_price') }}</span><span class="font-normal text-right">{{ pricePerUnit }}</span>
        </p>
        <template v-if="vouchers.length">
          <p v-for="voucher in vouchers" :key="voucher.id" class="flex justify-between w-full font-medium">
            <span>{{ voucher.details.name }}</span>
            <span class="font-normal text-tertiary-base text-right">{{ currencyString((voucher.totalPrice / 1000), voucher.currency) }}</span>
          </p>
        </template>
        <template v-if="event.information.loyaltyItems">
          <div v-for="loyalty in event.information.loyaltyItems" :key="loyalty.id" class="flex justify-between w-full font-medium">
            <span>{{ loyalty.programName }}</span>
            <div>
              <span v-if="loyalty.totalAmount" class="font-normal">
                ({{ loyalty.totalAmount }})
              </span>
              <span class="font-normal text-tertiary-base">+{{ loyalty.amount }}</span>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-if="isGiftCard">
      <p class="flex justify-between font-medium fs-18 border-b border-card-outline py-4 w-full">
        <span>{{ t('history.gift_card.payment_value') }}</span>
        <span>{{ currencyString(
          deciCentInUnit(Number(event.information.items[0].details?.giftCard?.credit?.amount)),
          event.information.items[0].details?.giftCard?.credit?.currency) }}</span>
      </p>
    </template>

    <!-- Payment method -->
    <div v-if="event.information.pspInformation" class="border-b border-card-outline py-4 w-full">
      <p class="flex justify-between w-full font-medium fs-16">
        <span>{{ t(getPaymentMethod) }}</span>
        <span class="font-normal text-right">{{ event.information.pspInformation.maskedProviderIdentifier }}</span>
      </p>
    </div>
    <p class="flex justify-between w-full font-medium fs-18 pt-4">
      <span>{{ t('history.details.total') }}</span>
      <span v-if="event.information.price?.isPriceHidden" class="font-normal"> --.-</span>
      <span v-else>{{ currencyString(event.information.price.amount, event.information.price.currency) }}</span>
    </p>
  </div>
</template>
<script setup lang="ts">
import StationImg from '@/components/StationImg.vue';
import RPIcon from '@/components/RPIcon/RPIcon.vue';

import { HistoryPaymentEventDetail } from '@/store/history/types';
import { useAuthStore } from '@/store/auth/auth';
import { computed, PropType, } from 'vue';
import { currencyString, dateString, currencySymbol, localeNumber } from '@/utils/utils';
import { PAYMENT_METHODS_NAMES_MAP } from '@/composable/usePayments';


const props = defineProps({
  event: {
    type: Object as PropType<HistoryPaymentEventDetail>,
    required: true
  }
});

const shortInfoPayments = ['GIFT_CARD', 'SUBSCRIPTION'];
const isShortInfo = computed(() => {
  return shortInfoPayments.includes(props.event.information.productType);
});

const isWash = computed(() => props.event.information.productType === 'CAR_WASH');
const isGiftCard = computed(() => props.event.information.productType === 'GIFT_CARD');

const formatDate = computed(() => {
  const startDate = new Date(props.event.sampleTime);
  return dateString(startDate);
});

const getPaymentMethod = computed(() => PAYMENT_METHODS_NAMES_MAP[props.event.information.pspInformation.providerMethod]);

const authStore = useAuthStore();

const pricePerUnit = computed(() => {
  const price = props.event.information.items[0].details.unitPrice;
  const currency = props.event.information.items[0].currency;
  const unit = props.event.information.items[0].details.unit;

  const currencyPerUnitSymbol = currencySymbol(currency);
  const parseNumber = localeNumber(price / 1000);
  return `${parseNumber} ${currencyPerUnitSymbol}/${unit}`;
});
const formatMileage = new Intl.NumberFormat(authStore.lang || undefined, {
  style: 'unit',
  unit: 'kilometer',
  unitDisplay: 'short',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
}).format(props.event.information.carOdometerM / 1000);

const getFullAddress = `${props.event.information?.poi.address.street} ${props.event.information?.poi.address.houseNumber}, 
  ${props.event.information?.poi.address.zip} ${props.event.information?.poi.address.city}`;

const vouchers = computed(() => {
  return props.event.information?.items.filter(item => item.totalPrice < 0);
});
const deciCentInUnit = (price: number) => Number(price / 1000).toFixed(2);
const goToNavigation = () => {
  if /* if we're on iOS, open in Apple Maps */
    ((navigator.platform.indexOf("iPhone") != -1) ||
    (navigator.platform.indexOf("iPod") != -1) ||
    (navigator.platform.indexOf("iPad") != -1))
    window.open(`http://maps.apple.com/?daddr=${decodeURI(getFullAddress)}`);

  else /* else use Google */
    window.open(`http://maps.google.com/maps?daddr=${decodeURI(getFullAddress)}`, '_blank');
};
</script>